import { Color as PersonaColor } from '@persona-id/design-tokens';
import Color from 'color';
import { ComponentPropsWithoutRef } from 'react';
import { FONT_WEIGHTS, HEIGHTS, SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import IconCaretDown from 'images/icons/caret-down.svg';
import { fieldSSTStyles } from 'inquiry/mixins/form';
import { colorCss } from 'lib/inquiryStyles';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { InputSelectComponent } from 'lib/interfaces/ui-components';

type InnerSelectProps = SelectProps & { hasValue: boolean };

const InnerSelect = styled('select')<InnerSelectProps>`
  ${(props) => fieldSSTStyles(props, props.theme, props.styles)};

  /* Hide default styling */
  appearance: none;
  &::-ms-expand {
    display: none;
  }

  height: ${HEIGHTS.input};
  opacity: unset; /* Fix vendor style setting this to 0.7 */

  padding-top: ${SPACINGS.base}px;
  padding-bottom: ${SPACINGS.base}px;
  padding-left: ${SPACINGS.base * 2}px;
  // NOTE: this prevents the long template id values from showing up behind the caret
  padding-right: ${SPACINGS.base * 4}px;

  ${({ hasValue, hasError, styles, theme }) => {
    // We need this complex logic because we cannot use ::placeholder for HTML selects.
    // This is intended to replicate the logic in placeholderSSTStyles().
    if (hasValue) return;
    if (styles?.['text-color']?.placeholder != null) {
      return css`
        font-weight: ${FONT_WEIGHTS.regular};
        color: ${colorCss(styles?.['text-color'].placeholder.base)};
        -webkit-text-fill-color: ${colorCss(styles?.['text-color'].placeholder.base)};

        &:disabled {
          color: ${colorCss(styles?.['text-color'].placeholder.disabled)};
          -webkit-text-fill-color: ${colorCss(styles?.['text-color'].placeholder.disabled)};
        }

        ${hasError &&
        `
          &:not(:focus) {
            color: ${colorCss(styles?.['text-color'].placeholder.errored)};
            -webkit-text-fill-color: ${colorCss(styles?.['text-color'].placeholder.errored)};
          }
        `}
      `;
    }

    // Default, non-SST styles
    const textColorRgb = Color(theme.colorFont).rgb();
    const textColorErroredRgb = Color(theme.colorError).rgb();
    return css`
      font-weight: ${FONT_WEIGHTS.regular};
      /* Placeholder styles have opacity 0.6, but we can't only target the placeholder element with <select/>.
           If this is updated, we also need to update the placeholder text color for input date */
      color: ${`rgba(${textColorRgb.red()}, ${textColorRgb.green()}, ${textColorRgb.blue()}, 0.6)`};
      -webkit-text-fill-color: ${`rgba(${textColorRgb.red()}, ${textColorRgb.green()}, ${textColorRgb.blue()}, 0.6)`};

      &:disabled {
        color: ${PersonaColor.CoreSlate800};
        -webkit-text-fill-color: ${PersonaColor.CoreSlate800};
      }

      ${hasError &&
      `
        &:not(:focus) {
          color: ${`rgba(${textColorErroredRgb.red()}, ${textColorErroredRgb.green()}, ${textColorErroredRgb.blue()}, 0.7)`};
          -webkit-text-fill-color: ${`rgba(${textColorErroredRgb.red()}, ${textColorErroredRgb.green()}, ${textColorErroredRgb.blue()}, 0.7)`};
        }
      `}
    `;
  }}
`;

const Wrapper = styled('div')`
  position: relative;
`;

const StyledIconCaretDown = styled(IconCaretDown)<SelectProps>`
  position: absolute;
  right: ${SPACINGS.base * 2}px;
  top: 50%;
  transform: translateY(-50%);

  display: inline-block;
  pointer-events: none;

  rect[fill],
  path[fill] {
    fill: ${({ styles, theme }) =>
      colorCss(styles?.['text-color']?.value?.base) ?? theme.colorFont};
  }
`;

export type SelectProps = ComponentPropsWithoutRef<'select'> &
  FormControlProps & {
    styles?: InputSelectComponent['styles'];
  };

const Select = ({ value, hasError, styles, ...props }: SelectProps) => {
  return (
    <Wrapper>
      <InnerSelect
        {...props}
        value={value}
        hasValue={Boolean(value)}
        hasError={hasError}
        styles={styles}
      />
      {!props.disabled && <StyledIconCaretDown styles={styles} />}
    </Wrapper>
  );
};

export default Select;
